import {
  Component, ChangeDetectionStrategy, AfterContentInit, TemplateRef, ViewChild, Inject, ViewEncapsulation, ViewContainerRef,
  Input
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';

import {
  TooltipDefaultValues, TooltipLuggageConfig, TooltipPriceConfig, TooltipType
} from '@design-system/components/templates/tooltip/entities/tooltip.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { PriceDetailCardType } from '@design-system/components/organisms/price-detail-card/entities/price-detail-card.types';
import { PriceDetailCardDsComponent } from '@design-system/components/organisms/price-detail-card/price-detail-card.component';
import { LuggageCardDsComponent } from '@design-system/components/molecules/luggage-card/luggage-card.component';
import { TypographyDsComponent } from '@design-system/components/atoms/typography/typography.component';

@Component({
  selector: 'app-ds-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [CommonModule],
})
export class TooltipDsComponent implements AfterContentInit {

  @Input() type = TooltipDefaultValues.Type;
  @Input() display = TooltipDefaultValues.Display;
  @Input() position = TooltipDefaultValues.Position;
  @Input() visible = TooltipDefaultValues.Visible;
  @Input() textAlign = TooltipDefaultValues.TextAlign;
  @Input() tooltip = '';
  @Input() price: TooltipPriceConfig | null = null;
  @Input() luggage: TooltipLuggageConfig | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('embedRef', { static: true }) emebedRef!: TemplateRef<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('vcr', { static: true, read: ViewContainerRef }) vcr!: ViewContainerRef;

  public left = 0;
  public top = 0;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
  ) { }

  ngAfterContentInit(): void {
    this.vcr?.clear();
    this.openComponent(this.emebedRef);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openComponent(content: any) {
    if (this.type === TooltipType.Text) {
      this.openTextType(content);
    } else if (this.type === TooltipType.Price) {
      this.openPriceType(content);
    } else if (this.type === TooltipType.Luggage) {
      this.openLuggageType(content);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openTextType(content: any): void {
    const ngContent = this.createNgContent(content);
    const options = { projectableNodes: ngContent };
    const factory = this.vcr?.createComponent(TypographyDsComponent, options);
    factory?.setInput('text', this.tooltip);
    factory?.setInput('fontFamily', TypographyFontFamily.SemiBold);
    factory?.setInput('size', TypographyFontSize.XXS);
    factory?.setInput('color', TypographyColor.Invert);
    factory?.hostView?.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openPriceType(content: any): void {
    const ngContent = this.createNgContent(content);
    const options = { projectableNodes: ngContent };
    const factory = this.vcr?.createComponent(PriceDetailCardDsComponent, options);
    factory?.setInput('type', PriceDetailCardType.Tooltip);
    factory?.setInput('title', this.price?.title);
    factory?.setInput('subtitles', this.price?.subtitles);
    factory?.setInput('rows', this.price?.rows);
    factory?.setInput('discount', this.price?.discount);
    factory?.setInput('total', this.price?.total);
    factory?.hostView?.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private openLuggageType(content: any): void {
    const ngContent = this.createNgContent(content);
    const options = { projectableNodes: ngContent };
    const factory = this.vcr?.createComponent(LuggageCardDsComponent, options);
    factory?.setInput('title', this.luggage?.title);
    factory?.setInput('rows', this.luggage?.rows);
    factory?.hostView?.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private createNgContent(content: any) {
    if (typeof content === 'string') {
      const element = this.document.createElement('div');
      element.innerHTML = content;
      return [[element]];
    }
    if (content instanceof TemplateRef) {
      const viewRef = content.createEmbeddedView(content.elementRef.nativeElement);
      return [viewRef.rootNodes];
    }
    return [[]];
  }
}
